// The editor's what-if (REQ-094, API-237): the draft against the last complete period of its
// kind, per scope instance — what was used and the day each warning and the limit would have been
// reached. Read-only on the server (nothing stored or audited), so it runs only on the button,
// never per keystroke; money in the answer goes through the money policy like everything else.
import { useEffect, useState } from "preact/hooks";
import { isAbort } from "../../api/client";
import { useApi } from "../../api/context";
import type { MoneyPolicy } from "../../api/org-context";
import { TitleWithHelp } from "../../primitives/field";
import { ErrorBanner } from "../../primitives/states";
import { DataTable, type Column } from "../../primitives/table";
import { asApiError, isNotAvailable, NOT_AVAILABLE, previewQuota, previewSummary, type PreviewRow, type PreviewSummary, type QuotaDraft } from "./model";
import type { ApiError } from "../../api/errors";

type PreviewState = { kind: "idle" } | { kind: "loading" } | { kind: "unavailable" } | { kind: "error"; error: ApiError } | { kind: "ready"; summary: PreviewSummary };

function Day({ value }: { value: string | null }) {
  return value === null ? <span class="quota-day quota-day-never">Never</span> : <span class="quota-day">{value}</span>;
}

function columns(summary: PreviewSummary): Column<PreviewRow>[] {
  const thresholdLabels = summary.rows[0]?.thresholds.map((t) => t.label) ?? [];
  return [
    { key: "scope", header: "Who", cell: (row) => row.scopeName, compare: (a, b) => a.scopeName.localeCompare(b.scopeName) },
    { key: "consumed", header: "Used", numeric: true, cell: (row) => row.consumed },
    ...thresholdLabels.map((label, index) => ({
      key: `t${index}`,
      header: `Reached ${label}`,
      cell: (row: PreviewRow) => <Day value={row.thresholds[index]?.reachedOn ?? null} />,
    })),
    { key: "limit", header: "Reached the limit", cell: (row) => <Day value={row.limitReachedOn} /> },
  ];
}

export function PreviewPanel({ orgId, draft, policy }: { orgId: string; draft: QuotaDraft | null; policy: MoneyPolicy }) {
  const client = useApi();
  const [state, setState] = useState<PreviewState>({ kind: "idle" });
  const [controller, setController] = useState<AbortController | null>(null);
  const key = JSON.stringify(draft);

  // A preview describes one draft; editing the form makes it stale, so it is dropped.
  useEffect(() => {
    controller?.abort();
    setState({ kind: "idle" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => () => controller?.abort(), [controller]);

  const run = () => {
    if (!draft) return;
    const next = new AbortController();
    setController(next);
    setState({ kind: "loading" });
    previewQuota(client, orgId, draft, next.signal)
      .then((preview) => setState({ kind: "ready", summary: previewSummary(preview, policy) }))
      .catch((error: unknown) => {
        if (isAbort(error)) return;
        setState(isNotAvailable(error) ? { kind: "unavailable" } : { kind: "error", error: asApiError(error) });
      });
  };

  return (
    <section class="quota-preview" aria-live="polite">
      <TitleWithHelp level={3} title="Preview" help="quotas.preview" />
      <p class="field-hint">What this cap would have done over the last complete period. Nothing is saved.</p>
      <button type="button" class="button button-secondary" disabled={!draft || state.kind === "loading"} onClick={run}>
        {state.kind === "loading" ? "Previewing…" : "Preview the last period"}
      </button>
      {!draft && <p class="field-hint">Fix the form to preview it.</p>}
      {state.kind === "unavailable" && <p class="banner banner-info">{NOT_AVAILABLE}</p>}
      {state.kind === "error" && <ErrorBanner error={state.error} onRetry={run} />}
      {state.kind === "ready" && (
        <>
          <p>
            <strong>{state.summary.period}</strong>
          </p>
          <ul class="quota-preview-lines">
            {state.summary.lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          {state.summary.unpriced && <p class="banner banner-warning">{state.summary.unpriced}</p>}
          <DataTable
            caption="Preview by scope"
            hideCaption
            columns={columns(state.summary)}
            rows={state.summary.rows}
            rowKey={(row) => row.key}
            emptyMessage="No usage in that period, so nobody would have been affected."
          />
        </>
      )}
    </section>
  );
}
